import React, { useEffect, useMemo, useState } from 'react';
import Box from '@mui/material/Box';
import Accordion from '@mui/material/Accordion';
import AccordionSummary from '@mui/material/AccordionSummary';
import AccordionDetails from '@mui/material/AccordionDetails';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import Typography from '@mui/material/Typography';
import {
  GoogleMap,
  useJsApiLoader,
  MarkerF,
  MarkerClustererF
} from '@react-google-maps/api';
import { getGlobalConfig } from '../../utils';
import { useQuery } from '@tanstack/react-query';
import Api from '../API';

const containerStyle = {
  width: '100%',
  height: '300px'
};

export default function GoogleMapApplicationLocation({ jobId }: { jobId: number }) {
  const { googleApiKey } = getGlobalConfig();

  const { isLoaded } = useJsApiLoader({
    id: 'google-map-script',
    googleMapsApiKey: googleApiKey || ''
  });

  const { data: job } = useQuery({
    queryKey: ['jobLocations', jobId],
    queryFn: async () => await Api.getApplicationLocations(jobId),
    enabled: true
  });

  const applicationIcon = (name: string) => {
    const initials = name
      .trim()
      .split(/\s+/)
      .slice(0, 2)
      .map(word => word[0].toUpperCase())
      .join("");
    return `
      <svg xmlns="http://www.w3.org/2000/svg" width="40" height="40">
        <!-- Outer white border -->
        <circle cx="20" cy="20" r="20" fill="white" />

        <!-- Inner circle -->
        <circle cx="20" cy="20" r="18" fill="#B9E5E5" />

        <!-- Initials -->
        <text x="50%" y="50%" text-anchor="middle" dy=".35em"
              font-family="Arial, sans-serif"
              font-size="14"
              font-weight="bold"
              fill="#0A3D62">
          ${initials}
        </text>
      </svg>
    `;
  };
  const [jobCenter, setJobCenter] = useState<google.maps.LatLngLiteral | undefined>(undefined);

  useEffect(() => {
    if (job) {
      setJobCenter({ lat: job.latitude, lng: job.longitude });
    }
  }, [job]);

  const [map, setMap] = useState<google.maps.Map | null>(null);

  // Fit bounds for job + all applications
  useEffect(() => {
    if (map && job?.latitude && job?.longitude && (job.applications?.length ?? 0) > 0) {
      const bounds = new google.maps.LatLngBounds();
      bounds.extend({ lat: job.latitude, lng: job.longitude });
      job.applications.forEach((app: { latitude: number | null; longitude: number | null }) => {
        if (app.latitude !== null && app.longitude !== null) {
          bounds.extend({ lat: app.latitude, lng: app.longitude });
        }
      });
      map.fitBounds(bounds, {
        top: 50,
        right: 50,
        bottom: 50,
        left: 50
      });
    } else if (map && job) {
      map.setCenter({ lat: job.latitude, lng: job.longitude });
      map.setZoom(12); // fallback if no applications
    }
  }, [map, job]);

  if (!job?.latitude || !job?.longitude || job?.applications?.length === 0) {
    return null;
  }

  return (
    <Box sx={{ width: '100%', marginBottom: '10px' }}>
      <Accordion
        sx={{
          boxShadow: 'none',
          border: 'none',
          '&:before': {
            display: 'none'
          }
        }}
      >
        <AccordionSummary
          expandIcon={<ExpandMoreIcon />}
          aria-controls="panel1-content"
          id="panel1-header"
          sx={{
            '&:focus': { outline: 'none' }
          }}
        >
          <Typography component="span" fontWeight={'bold'}>
            Application Locations
          </Typography>
        </AccordionSummary>
        <AccordionDetails>
          {isLoaded ? (
            <GoogleMap
              mapContainerStyle={containerStyle}
              zoom={10}
              onLoad={(m) => setMap(m)}
              options={{
                streetViewControl: false,
                mapTypeControl: false,
                styles: [
                  { featureType: "poi", stylers: [{ visibility: "off" }] }, // remove points of interest to avoid clutter
                  { featureType: "road", elementType: "geometry", stylers: [{ weight: 0.5 }]}, // smaller value = thinner road
                ]
              }}
            >
              <MarkerF
                position={jobCenter || { lat: 0, lng: 0 }}
                zIndex={9999}
                title={job.title}
              />
              <MarkerClustererF options={{ maxZoom: 16 }}>
                {(clusterer: any) =>
                  job.applications
                    .filter(
                      (app: { latitude: number | null; longitude: number | null }) =>
                        app.latitude !== null && app.longitude !== null
                    )
                    .map(
                      (app: {
                        id: number;
                        latitude: number;
                        longitude: number;
                        candidate_name: string;
                      }) => (
                        <MarkerF
                          key={app.id}
                          icon={{
                            url: "data:image/svg+xml;charset=UTF-8," + encodeURIComponent(applicationIcon(app.candidate_name)),
                          }}
                          position={{ lat: app.latitude, lng: app.longitude }}
                          clusterer={clusterer}
                          title={app.candidate_name}
                        />
                      )
                    )
                }
              </MarkerClustererF>
            </GoogleMap>
          ) : (
            <Typography>Loading...</Typography>
          )}
        </AccordionDetails>
      </Accordion>
    </Box>
  );
}
